<template>
    <page-layout>
        <template #header>
            <step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :title="pageTitle"
                :use-exit-modal="false"
                :is-loading="isLoading"
                :skip-steps="isBriefAccepted"
                :is-preview="isPreview"
                :exit-route="exitRoute"
            />
        </template>
        <template #default>
            <g-loader v-if="isLoading" type="fixed" />
            <div v-else>
                <g-button
                    v-if="isBriefAccepted"
                    class="margin-bottom-large"
                    icon="angle-left"
                    theme="live"
                    size="small"
                    @click="handleBack"
                >
                    Back
                </g-button>

                <float-on-mask v-if="showTagsBox" tag="div" :float-up="isBriefAccepted">
                    <g-typography
                        tag="h4"
                        :value="tagsOverview"
                        class="break-new-line margin-bottom-small"
                        font-weight="semibold"
                    />

                    <tags-box
                        :tags="tags"
                        :custom-description="tagsOverview"
                        :tag-post="tagPost"
                        data-testid="tags_box"
                    />
                </float-on-mask>

                <div v-if="showNetworkMarketplaceInvitationBox">
                    <network-marketplace-invitation-box
                        :creator-licensing-configs="brief.creator_licensing_configs"
                        :content-requests="brief.network_content_requests"
                        :latest-ttocm-campaign-history="latestTtocmCampaignHistory"
                    />
                </div>

                <div v-if="instructionsText" class="margin-bottom-normal">
                    <g-typography
                        tag="h4"
                        value="Task Instructions"
                        class="margin-bottom-small break-new-line"
                        font-weight="semibold"
                    />

                    <div v-html="$sanitize(instructionsText)" />
                </div>

                <div v-if="brandAssets.length" class="margin-bottom-normal">
                    <g-typography
                        tag="h4"
                        :value="brandAssetsText"
                        class="margin-bottom-small break-new-line"
                        font-weight="semibold"
                    />

                    <div v-for="asset in brandAssets" :key="asset.id">
                        <activations-brand-asset :asset="asset" class="margin-bottom-normal" />
                    </div>
                </div>

                <div v-if="requiredTasks.length">
                    <g-typography
                        tag="h4"
                        value="Your Tasks"
                        class="break-new-line margin-bottom-small"
                        font-weight="semibold"
                    />

                    <activation-task-card
                        :brief="brief"
                        :tasks="requiredTasks"
                        :panels-open="true"
                        :context="context"
                        @remove-task="removeTask"
                        @add-task="addTask"
                    />
                </div>
                <div v-if="optionalTasks.length">
                    <g-typography
                        tag="h4"
                        value="Optional Tasks"
                        class="break-new-line margin-bottom-small"
                        font-weight="semibold"
                    />

                    <activation-task-card
                        :brief="brief"
                        :tasks="optionalTasks"
                        :optional="true"
                        :context="context"
                        @add-task="addTask"
                    />
                </div>

                <content-approval-onboarding-drawer
                    :visible="isContentApprovalOnboardingDrawerVisible"
                    @confirm="handleContentApprovalOnboardingConfirmed"
                />
            </div>
        </template>
        <template #footer>
            <step-footer
                v-if="isBriefEditable"
                :is-next-disabled="false"
                :step="step"
                :loading="isCompletingBrief"
                :use-exit-modal="false"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :is-preview="isPreview"
                @patch-step="patchStep"
                @go-next-step="goNextStep"
                @go-previous-step="goPreviousStep"
            />
        </template>
    </page-layout>
</template>

<script>
    import { delay, get } from 'lodash';
    import StepFooter from './partials/StepFooter.vue';
    import StepHeader from './partials/StepHeader.vue';
    import StepError from './partials/StepError.vue';
    import PageLayout from '../../layouts/PageLayout.vue';
    import FloatOnMask from '../../library/FloatOnMask.vue';
    import ErrorMessageCard from '../activation-briefs/partials/ErrorMessageCard.vue';
    import NetworkMarketplaceInvitationBox from '../activation-briefs/partials/NetworkMarketplaceInvitationBox.vue';
    import TagsBox from '../activation-briefs/partials/TagsBox.vue';
    import ActivationTaskCard from './partials/ActivationTaskCard.vue';
    import ActivationsBrandAsset from '../../library/ActivationsBrandAsset.vue';
    import ContentApprovalOnboardingDrawer from './partials/content-approval/ContentApprovalOnboardingDrawer.vue';
    import ActivationTasksMixin from '../activation-briefs/mixins/ActivationTasksMixin';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import { getCustomizableText, getCustomizableTitle } from '../../../helpers/activations/default-texts';
    import { BRAND_ASSET_FILE_TYPE } from '../../../constants/activation-files';
    import { getActivationFiles } from '../../../helpers/activations/activation-files';
    import useNetworkMarketplace from '../../../../src/composables/use-network-marketplace';

    export default {
        name: 'ActivationBriefTasksView',
        components: {
            ActivationTaskCard,
            ActivationsBrandAsset,
            ContentApprovalOnboardingDrawer,
            StepFooter,
            StepHeader,
            StepError,
            PageLayout,
            FloatOnMask,
            NetworkMarketplaceInvitationBox,
            TagsBox,
            ErrorMessageCard
        },
        mixins: [ActivationBriefMixin, ActivationTasksMixin],
        props: {
            brief: {
                type: Object,
                default: null
            },
            contact: {
                type: Object,
                default: null
            },
            step: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingBrief: {
                type: Boolean,
                default: false
            },
            errors: {
                type: Object,
                default: () => ({})
            },
            errorMessage: {
                type: String,
                default: ''
            },
            context: {
                type: String,
                default: ''
            }
        },
        data() {
            const exitRoute =
                this.context === 'summary' ? 'v2.activation.tasks.summary' : 'v2.activation.brief.overview';

            return {
                isContentApprovalOnboardingDrawerVisible: false,
                exitRoute
            };
        },
        computed: {
            brandAssetsText() {
                return getCustomizableTitle(this.brief, 'brand_assets_title');
            },
            pageTitle() {
                return getCustomizableTitle(this.brief, 'live_tasks_title');
            },
            tagsOverview() {
                return getCustomizableText(this.brief, 'live_task_description');
            },
            instructionsText() {
                return getCustomizableText(this.brief, 'live_task_instructions');
            },
            stepNumber() {
                return get(this, 'step.meta.stepNumber', 0);
            },
            totalSteps() {
                return get(this, 'step.meta.totalSteps', 0);
            },
            isCompleted() {
                return get(this, 'step.is_complete', false);
            },
            tasks() {
                return this.isBriefAccepted ? this.briefTasks : this.requiredTasks;
            },
            brandAssets() {
                return getActivationFiles(this.brief, BRAND_ASSET_FILE_TYPE);
            },
            showNetworkMarketplaceInvitationBox() {
                return useNetworkMarketplace().showNetworkMarketplaceInvitationBox(this.brief);
            },
            latestTtocmCampaignHistory() {
                return useNetworkMarketplace().getLatestTtocmCampaignHistory(this.brief, this.$root.briefs);
            }
        },
        methods: {
            goNextStep() {
                this.$emit('go-next-step', this.step, { task_ids: this.tasksToAdd });
            },
            goPreviousStep() {
                this.$emit('go-previous-step', this.step, { task_ids: this.tasksToAdd });
            },
            patchStep() {
                this.$emit('patch-step', { task_ids: this.tasksToAdd });
            },
            handleBack() {
                this.$router.push({
                    name: this.exitRoute
                });
            },
            contentApprovalOnboarding() {
                if (
                    this.isBriefAccepted &&
                    this.contentApprovalTasks?.length &&
                    !this.contact?.content_approval_onboarding_viewed
                ) {
                    this.isContentApprovalOnboardingDrawerVisible = true;
                }
            },
            handleContentApprovalOnboardingConfirmed() {
                this.isContentApprovalOnboardingDrawerVisible = false;
                this.$emit('hide-content-approval-onboarding');
            }
        },
        watch: {
            isLoading(value) {
                if (!value) {
                    delay(this.contentApprovalOnboarding, 800);
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .no-margins {
        & ::v-deep p:last-child {
            margin: 0;
        }
    }

    .padding-top-normal {
        padding-top: $g-spacing-5;
    }

    .task-delivered {
        color: $g-color-green-5;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>
